import React, { useCallback, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import Config from "@skin_config";
import { POPUPS } from "@common/constants/popups";
import { PopupContext } from "@app/dev/contexts/popup";
import { useTranslation } from "@common/hooks/translation";
import { useBalance } from "@app/dev/terminal/hooks/balance";
import TooltipDropDown from "@common/components/tooltipDropDown";
import OctopayLogo from "@app/dev/images/deposit/octopay-logo.png";
import { DepositModeEnum } from "@app/dev/constants/enums/depositMode";
import { TerminalDepositModesEnum } from "@app/dev/constants/enums/terminalDepositModes";
import { CashoutMoney } from "@app/dev/actions/terminal";
import { getAuthData } from "@common/helpers/auth";
import { isMultiDepositModeEnabled } from "@app/dev/helpers/multiDepositMode";

const CashoutButtonStandard = ({ onClick, showAsDrowpdownHeader = false }) => {
  const t = useTranslation();

  return (
    <div className="cashout" onClick={onClick}>
      <i className="global-icon icon-cashout" />
      <p>{t("Cash out")}</p>
      {showAsDrowpdownHeader && <i className="global-icon icon-thin-arrow-down" />}
    </div>
  );
};

export const CashoutButton = () => {
  const terminalBalance = useBalance();
  const [{ openPopup }] = useContext(PopupContext);
  const { depositMode, loggedIn, loggedInByRFID } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const userLoggedin = loggedInByRFID || loggedIn;
  const { playerInfo, depositMode: terminalDepositMode, verification } = useSelector((state) => state.terminal);

  const openCashoutPopup = useCallback(
    (clearStateBeforeCashout) => {
      if (depositMode === DepositModeEnum.Cash || terminalBalance === 0) {
        if (Config.cashoutMethods.length && userLoggedin) {
          openPopup(POPUPS.CASHOUT_METHODS);
        } else {
          openPopup(POPUPS.CASHOUT, { clearStateBeforeCashout });
        }
      } else openPopup(POPUPS.CASHOUT_BY_BANK_CARD);
    },
    [depositMode, openPopup, terminalBalance, userLoggedin]
  );

  const isMultiCashoutOptions = terminalDepositMode === TerminalDepositModesEnum.Ticket && isMultiDepositModeEnabled()

  const cashoutOptions = useMemo(() => {
    const options = [
      {
        buttonName: 'Cash out',
        iconName: 'icon-cashout',
        callback: () => openCashoutPopup(isMultiCashoutOptions),
        enabled: true,
      },
      isMultiCashoutOptions && {
        buttonName: 'Okto pay',
        pic: OctopayLogo,
        callback: () => {
          if (playerInfo?.PersonalId) dispatch(CashoutMoney(null));
          else openPopup(POPUPS.CASHOUT_BY_OCTO);
        },
        enabled: true,
      },
      Config.balanceTransfer.enableTransferToUser && {
        buttonName: 'Transfer',
        iconName: 'icon-transactions',
        callback: () => openPopup(POPUPS.TRANSFER_TO_USER),
        enabled: true,
      },
    ].filter(Boolean);

    return options.filter(Boolean);
  }, [dispatch, terminalDepositMode, openCashoutPopup, openPopup, playerInfo, isMultiCashoutOptions]);

  const isVerificationDone = !Config.main.hasVerification || verification.isVerified;

  if (
    (loggedIn && Config.header.disableCashoutAfterLogin) ||
    Config.header.disableCashout ||
    !isVerificationDone ||
    !terminalBalance
  )
    return null;

  if (cashoutOptions.length > 1) {
    return (
      <TooltipDropDown
        className="header-item cashout-options"
        options={cashoutOptions}
        header={<CashoutButtonStandard showAsDrowpdownHeader />}
      />
    );
  }

  return <CashoutButtonStandard onClick={() => cashoutOptions[0]?.callback()} />;
};
